import React, { useState } from 'react';
import { sortableHandle } from 'react-sortable-hoc';

import styles from './CssModules/PolicyRowHeader.module.css';
import commonStyles from './CssModules/CommonPolicyStyles.module.css';
import switchContainerStyles from '../common/css/SwitchContainer.module.css';
import { POLICIES_HITS_TIME_UNITS_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import PersonsCounter from '../Controls/PersonCounter';
import menuIcon from '../common/NewImages/3-dots-menu.svg';
import copyIcon from '../common/NewImages/copy.svg';
import powerIcon from '../common/NewImages/power-button.svg';
import trahsIcon from '../common/NewImages/trash.svg';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import ConfirmationMessage from '../Controls/ConfirmationMessage';
import { FeatureLock } from '../common/LockedFeatures';

const DragHandle = sortableHandle(() => <i disabled={true} className={`${styles.dragIcon} fa fa-bars `}></i>);
const DisabledDragHandle = sortableHandle(({ isExpanded }) => (
    <i
        title={`Reorder is disabled when ${isExpanded ? 'policy is expanded' : 'filtering'}`}
        disabled={true}
        className={`${styles.dragIcon} ${styles.disabledDrag} fa fa-bars`}></i>
));

const PolicyRowHeader = ({
    policyTitleData,
    editedPolicyData,
    isExpanded,
    onHeaderClicked,
    isNewPolicies,
    isEditor,
    isSortable,
    setShowStateChangeModal,
    filteredData,
    isDirty,
    policyConfiguration,
    onDuplicatePolicyClicked,
    handleDeletePolicy,
    shouldDisplayActions = true,
    featureLockDisplayOptions = { enabled: true }
}) => {
    const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);

    const onDeletePolicyClicked = async () => {
        const deletionResults = await handleDeletePolicy(policyTitleData.id);
        if (!deletionResults) {
            setShowDeletePolicyModal(false);
        }
    };

    const policiesHitsUnitsText = Object.values(POLICIES_HITS_TIME_UNITS_TYPES).find(
        (timeUint) => policyConfiguration.policiesHitsTimeUnit === timeUint.value
    );
    return (
        <FeatureLock displayOptions={featureLockDisplayOptions}>
            <div
                className={`${commonStyles.basePolicyHeader} ${styles.policyHeader} ${
                    !policyTitleData.state && !isExpanded ? styles.inactivePolicyHeader : ''
                } ${isExpanded ? styles.openPolicyHeader : ''} `}
                onClick={() => onHeaderClicked(policyTitleData.id)}>
                <div className={styles.leftSide}>
                    {isSortable && !filteredData && !isExpanded && <DragHandle />}
                    {isSortable && filteredData && <DisabledDragHandle isExpanded={false} />}
                    {isSortable && !filteredData && isExpanded && <DisabledDragHandle isExpanded={true} />}
                    <div
                        className={`${switchContainerStyles.switchContainer} ${
                            !isEditor || isNewPolicies ? switchContainerStyles.disabled : ''
                        }`}
                        onClick={(e) => e.stopPropagation()}>
                        <label
                            htmlFor={'switch' + policyTitleData.id}
                            id={'switch' + policyTitleData.id}
                            onClick={() => setShowStateChangeModal(true)}>
                            <input
                                checked={policyTitleData.state}
                                onChange={() => {}}
                                className={switchContainerStyles.switch}
                                type="checkbox"
                            />
                            <div>
                                <div></div>
                            </div>
                        </label>
                    </div>

                    <div className={`${styles.policyName} w-[30rem]`}>{policyTitleData.name}</div>

                    {isDirty && <div className={styles.editedPolicy}>Edited</div>}
                    {!isNewPolicies && !isDirty && (
                        <div className="pl-4">
                            <PersonsCounter number={editedPolicyData.users_sum} disabled={!policyTitleData.state} />
                        </div>
                    )}
                </div>
                <div className={styles.rightSide}>
                    <div className={`${styles.rightSideItem} text-sf-light-700`}>
                        {policyConfiguration.enablePoliciesHits &&
                            policiesHitsUnitsText &&
                            policyTitleData.id >= 0 &&
                            !isNaN(policyTitleData.id) && (
                                <div>
                                    <span>
                                        {`Applied ${policyTitleData.hits} time${
                                            policyTitleData.hits === 1 ? '' : 's'
                                        } (${policyConfiguration.policiesHitsThreshold} ${
                                            policyConfiguration.policiesHitsThreshold > 1
                                                ? policiesHitsUnitsText.label.toLocaleLowerCase()
                                                : policiesHitsUnitsText.label.slice(0, -1).toLocaleLowerCase()
                                        })`}
                                    </span>
                                </div>
                            )}
                    </div>
                    {shouldDisplayActions && (
                        <div className="px-4" onClick={(e) => e.stopPropagation()}>
                            <DropdownButton
                                id={`policyActions-${policyTitleData.id}`}
                                noCaret
                                pullRight
                                className="!p-0 !border-transparent !bg-transparent"
                                title={<img src={menuIcon} alt="" />}>
                                <MenuItem onClick={() => setShowStateChangeModal(true)}>
                                    <div className="flex gap-2">
                                        <img src={powerIcon} alt="" />
                                        <div>{policyTitleData.state ? 'Deactivate' : 'Activate'}</div>
                                    </div>
                                </MenuItem>
                                <MenuItem onClick={onDuplicatePolicyClicked}>
                                    <div className="flex gap-2">
                                        <img src={copyIcon} alt="" />
                                        <div>Duplicate</div>
                                    </div>
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setShowDeletePolicyModal(true);
                                    }}>
                                    <div className="flex gap-2">
                                        <img src={trahsIcon} alt="" />
                                        <div>Delete</div>
                                    </div>
                                </MenuItem>
                            </DropdownButton>
                        </div>
                    )}
                    <div className={styles.rightSideItem}>
                        {isExpanded && <i className={`fa fa-angle-up ${styles.collapseIcon}`}></i>}
                        {!isExpanded && <i className={`fa fa-angle-down ${styles.expandIcon}`}></i>}
                    </div>
                </div>
                {showDeletePolicyModal && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <ConfirmationMessage
                            title={'Delete Policy'}
                            body={`Are you sure you want to delete "${policyTitleData.name}" ?`}
                            approveText={'Delete'}
                            cancelText={'Cancel'}
                            approveStyle={'danger'}
                            OnCancel={() => setShowDeletePolicyModal(false)}
                            OnApprove={onDeletePolicyClicked}
                        />
                    </div>
                )}
            </div>
        </FeatureLock>
    );
};

export default PolicyRowHeader;
